import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"
import { OptionType, OptionTypes } from "shared-ts/types/tender/tender.types"

import { AppState } from "app/store"
import { CustomerState } from "features/pos/transactions/CustomerSidebar/CustomerSidebar"

// TenderSelection is the drawer's tender choice mirrored into the slice for
// consumers outside the drawer (e.g. surcharge math). Interac is a mode of
// card payments, so only the card variant carries it.
export type TenderSelection =
  | { type: typeof OptionTypes.CARD; isInterac: boolean }
  | { type: Exclude<OptionType, typeof OptionTypes.CARD> }

export type BillsState = {
  customerId?: UUID
  notes: string
  cardSurchargeRate?: Decimal
  isCustomerCardSurchargeExempt?: boolean
  tenderSelection?: TenderSelection
}

export const initialState: BillsState = {
  notes: "",
  cardSurchargeRate: undefined,
}

// checkIsEdited returns whether the state is edited from its initial state.
// Unlike with sales, orders, & transfers, with bill payments we do not have
// to worry about editing an existing one as they have only two states: new
// & paid (where paid is terminal).
export function checkIsEdited(state: BillsState): boolean {
  if (state.notes !== initialState.notes) {
    return true
  }

  return false
}

export const billsSlice = createSlice({
  name: "bills",
  initialState,
  reducers: {
    resetBillForm: (state) => {
      const cardSurchargeRate = state.cardSurchargeRate

      for (const key of Object.keys(state)) {
        delete state[key as keyof BillsState]
      }

      Object.assign(state, {
        ...initialState,
        cardSurchargeRate,
      })
    },
    customerSelected(state, action: PayloadAction<CustomerState>) {
      state.customerId = action.payload?.customerId
        ? UUID.fromPB(action.payload?.customerId)
        : undefined
      state.isCustomerCardSurchargeExempt =
        action.payload?.isCustomerCardSurchargeExempt
    },
    setNotes(state, action: PayloadAction<string>) {
      state.notes = action.payload
    },
    setTenderOption(state, action: PayloadAction<OptionType>) {
      state.tenderSelection =
        action.payload === OptionTypes.CARD
          ? { type: action.payload, isInterac: false }
          : { type: action.payload }
    },
    setIsInterac(state, action: PayloadAction<boolean>) {
      if (state.tenderSelection?.type === OptionTypes.CARD) {
        state.tenderSelection.isInterac = action.payload
      }
    },
    setCardSurchargeRate(state, action: PayloadAction<Decimal>) {
      state.cardSurchargeRate = action.payload
    },
  },
})

export const {
  resetBillForm,
  customerSelected,
  setCardSurchargeRate,
  setNotes,
  setTenderOption,
  setIsInterac,
} = billsSlice.actions

export const selectCustomerId = (state: AppState) => state.bills.customerId

export const selectCardSurchargeRate = (state: AppState) =>
  state.bills.cardSurchargeRate

export const selectNotes = (state: AppState) => state.bills.notes

export const selectIsEdited = (state: AppState) =>
  checkIsEdited(state.bills as BillsState)

export const selectTenderOption = (state: AppState) =>
  state.bills.tenderSelection?.type

// Interac (debit) is legally prohibited from carrying a card surcharge.
export const selectShouldApplyCardSurcharge = (state: AppState) =>
  state.bills.tenderSelection?.type === OptionTypes.CARD &&
  !state.bills.tenderSelection.isInterac

export const selectIsCustomerCardSurchargeExempt = (state: AppState) =>
  state.bills.isCustomerCardSurchargeExempt
